import React, { FC, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { formatDate } from 'utils/format';
import { Typography, Box, Alert, Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { Department } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useTranslation } from 'react-i18next';
import CellOrdersDialog from './cell-orders-dialog.component';
import { isSameDay, getDepartmentDaySteps, getCellCounts, getLabHourRange } from '../utils';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';
import ProductionDashboardStepSchedule from '../step-schedule/production-dashboard-step-schedule.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import DetailGridTable from './detail-grid-table.component';
import DetailOrdersTable from './detail-orders-table.component';
import { printOrdersList } from './print-orders-list';

type ProductionDashboardDetailProps = {
    department: Department;
    date: Date;
    scheduledSteps: ScheduledStep[];
    onBack: () => void;
    selectedStep: string | null;
    onShowSchedule: (stepId: string) => void;
    onBackFromSchedule: () => void;
    onStepChanged?: () => void;
};

const ProductionDashboardDetail: FC<ProductionDashboardDetailProps> = ({
    department,
    date,
    scheduledSteps,
    onBack,
    selectedStep,
    onShowSchedule,
    onBackFromSchedule,
    onStepChanged,
}) => {
    const { t: translate } = useTranslation(['production-dashboard']);

    const [dialogData, setDialogData] = useState<{
        steps: ScheduledStep[];
        stepName: string;
        hour: string;
    } | null>(null);

    const handleCellClick = (cellSteps: ScheduledStep[], stepName: string, hour: number) => {
        if (cellSteps.length === 0) return;
        setDialogData({ steps: cellSteps, stepName, hour: `${hour.toString().padStart(2, '0')}:00` });
    };

    // The cell's steps as of the latest fetch, so a workflow action taken from the cell
    // dialog's plan overview updates its rows while it stays open (DD-3802).
    const cellDialogSteps = useMemo(
        () =>
            (dialogData?.steps || []).flatMap((cellStep) => {
                const freshStep = scheduledSteps.find(
                    (step) => step.orderId === cellStep.orderId && step.stepId === cellStep.stepId,
                );
                return freshStep ? [freshStep] : [];
            }),
        [dialogData, scheduledSteps],
    );

    const gridSteps = useMemo(
        () => scheduledSteps.filter((s) => s.department === department.name && isSameDay(s.deadline, date)),
        [scheduledSteps, department.name, date],
    );

    // The hour grid places steps by deadline hour, so it shows only those due this day.
    // The day's totals and order list also count overdue work carried into today,
    // like the overview cell (DD-3802).
    const daySteps = useMemo(
        () => getDepartmentDaySteps(scheduledSteps, department.name, date),
        [scheduledSteps, department.name, date],
    );

    const orderListSteps = useMemo(() => {
        const statusPriority = (status: ScheduleStatus) =>
            [ScheduleStatus.LATE, ScheduleStatus.PAUSED, ScheduleStatus.ON_SCHEDULE, ScheduleStatus.EARLY].indexOf(
                status,
            );

        const byOrder = new Map<string, ScheduledStep & { totalProductionHours: number }>();
        for (const step of daySteps) {
            const existing = byOrder.get(step.orderId);
            if (existing) {
                existing.totalProductionHours += step.estimatedProductionHours || 0;
                if (statusPriority(step.scheduleStatus) < statusPriority(existing.scheduleStatus)) {
                    existing.scheduleStatus = step.scheduleStatus;
                }
            } else {
                byOrder.set(step.orderId, {
                    ...step,
                    totalProductionHours: step.estimatedProductionHours || 0,
                });
            }
        }

        return Array.from(byOrder.values()).sort((a, b) => {
            return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        });
    }, [daySteps]);

    const dayCounts = useMemo(
        () => getCellCounts(daySteps, department.productionHourLimit),
        [daySteps, department.productionHourLimit],
    );

    const openingHours = useReactiveVar(currentOrgVar)?.openingHours;

    const hours = useMemo(() => {
        const { open, close } = getLabHourRange(date, openingHours);
        let minHour = open;
        let maxHour = close - 1;
        if (gridSteps.length > 0) {
            const hoursArray = gridSteps.map((s) => s.deadline.getHours());
            minHour = Math.min(minHour, ...hoursArray);
            maxHour = Math.max(maxHour, ...hoursArray);
        }
        const range: number[] = [];
        for (let h = minHour; h <= maxHour; h++) range.push(h);
        return range;
    }, [gridSteps, date, openingHours]);

    // selectedStep is a step id (DD-3786). One not in this department, e.g. from an
    // older name-based link, shows the department view instead.
    const selectedWorkflowStep = department.workflowSteps.find((ws) => ws._id === selectedStep);

    if (selectedWorkflowStep) {
        return (
            <ProductionDashboardStepSchedule
                stepId={selectedWorkflowStep._id}
                stepName={selectedWorkflowStep.name}
                departmentName={department.name}
                date={date}
                scheduledSteps={scheduledSteps}
                onBack={onBackFromSchedule}
            />
        );
    }

    return (
        <>
            <Box mb={2}>
                <PageTitle
                    headingStyle="h2"
                    title={`${department.name} - ${formatDate(date, 'date')}`}
                    showBackButton
                    onBackArrow={onBack}
                    disableGutter
                />
            </Box>
            {dayCounts.isOverLimit && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('production-dashboard:PRODUCTION_LIMIT_EXCEEDED', {
                        actual: dayCounts.totalProductionHours.toFixed(1),
                        limit: department.productionHourLimit,
                    })}
                </Alert>
            )}
            <DetailGridTable
                department={department}
                gridSteps={gridSteps}
                hours={hours}
                onCellClick={handleCellClick}
                onShowSchedule={onShowSchedule}
            />
            <Box mt={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h3">{translate('production-dashboard:ORDERS_FOR_DAY')}</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PrintIcon />}
                        disabled={orderListSteps.length === 0}
                        onClick={() => printOrdersList(orderListSteps, department.name, date, translate)}
                    >
                        {translate('production-dashboard:PRINT_LIST')}
                    </Button>
                </Box>
                <DetailOrdersTable steps={orderListSteps} date={date} onStepChanged={onStepChanged} />
            </Box>
            <CellOrdersDialog
                open={!!dialogData}
                onClose={() => setDialogData(null)}
                steps={cellDialogSteps}
                stepName={dialogData?.stepName || ''}
                hour={dialogData?.hour || ''}
                onStepChanged={onStepChanged}
            />
        </>
    );
};

export default ProductionDashboardDetail;
